import FJConstant from '../../constants/FJConstant';
import FJUtil from '../../util/FJUtil';

class FJVideoPlayBase {
    _source = null; // 视频Video 标签
    _sourceInfo = null; // 视频源信息
    _sourceUrl = null; // 视频源url

    _sourceBlob = null; // 视频源blob

    // 状态  FJConstant.LOADING 加载中 FJConstant.ERROR 加载错误 FJConstant.SUCCESS 加载成功
    _loadStatus = FJConstant.DEFAULT;
    _isPlaying = false;
    _duration = 0;

    constructor() {}

    /**
     * @desc 跳转到指定帧
     * @param {Number} time
     */
    async seek(time, callback) {
        if (!this._source) return;
        const seeked = await new Promise(resolve => {
            this._source.removeEventListener('seeked', this._seekedListener);

            this._seekedListener = () => {
                resolve && resolve(true);
            };
            this._source.addEventListener('seeked', this._seekedListener);
            this._source.currentTime = time;

            if (this._isPlaying) {
                this._playTime = time;
            }
        });

        if (seeked) {
            callback && callback();
        }
    }

    /**
     * @desc 绘制画面
     */
    async draw(viewCtx, rect) {
        if (!viewCtx || this._loadStatus !== FJConstant.SUCCESS || !this._source) return;

        viewCtx.drawImage(this._source, rect.x, rect.y, rect.width, rect.height);
    }

    /**
     * @desc 播放视频
     */
    async play(callback, pauseCallback) {
        if (this._isPlaying) return;
        this._isPlaying = true;
        if (this._source) {
            let requestAnimationTime;
            this._playTime = this._source.currentTime; // 0
            await this._source.play();

            let __playing = timestamp => {
                if (!requestAnimationTime) requestAnimationTime = timestamp;

                this._playTime += (timestamp - requestAnimationTime) / 1000;
                requestAnimationTime = timestamp;

                if (this._playTime >= 0 && this._playTime <= this._duration) {
                    callback && callback(this._playTime);
                    this.draw();
                }

                if (this._playTime >= this._duration) {
                    this._isPlaying && this.pause();
                    pauseCallback && pauseCallback(this._source.currentTime);
                } else {
                    this._requestTimer = window.requestAnimationFrame(__playing);
                }
            };

            if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
            this._requestTimer = window.requestAnimationFrame(__playing);
        }
    }

    /**
     * @desc 暂停视频
     */
    async pause() {
        if (!this._isPlaying || !this._source) return;

        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        await this._source.pause();
        this._isPlaying = false;
    }

    /**
     * @desc 设置音量
     * @param {Number} volume
     */
    setVolume(volume) {
        this._source.volume = volume;
    }

    /**
     * @desc 设置视频源
     * @param {Object} source
     */
    async setSource(src) {
        try {
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            this._loadStatus = FJConstant.LOADING;
            this._sourceUrl = src;

            const sourceInfo = await new Promise((resolve, reject) => {
                const __onCanplay = () => {
                    if (video.duration === Infinity) {
                        video.currentTime = 10000;
                    } else {
                        __onSuccess();
                    }
                };

                const __onSuccess = () => {
                    const fileData = {
                        width: video.videoWidth,
                        height: video.videoHeight,
                    };
                    this._duration = video.duration;
                    this._loadStatus = FJConstant.SUCCESS;

                    video.removeEventListener('seeked', __onSuccess);
                    video.removeEventListener('canplay', __onCanplay);
                    video.removeEventListener('error', __onError);

                    resolve(fileData);
                };

                const __onError = () => {
                    this._loadStatus = FJConstant.ERROR;
                    video.removeEventListener('seeked', __onSuccess);
                    video.removeEventListener('canplay', __onCanplay);
                    video.removeEventListener('error', __onError);
                    reject();
                };

                video.addEventListener('seeked', __onSuccess);
                // 监听元数据加载完成事件
                video.addEventListener('canplay', __onCanplay);
                video.addEventListener('error', __onError);

                video.src = src;
            });

            this._source = video;
            this._sourceInfo = sourceInfo;
        } catch (error) {
            this._loadStatus = FJConstant.ERROR;
            throw error;
        }
    }

    clearAll() {
        this.pause();
        this._source = null;
        this._sourceInfo = null;
        this._loadStatus = FJConstant.DEFAULT;
        this._isPlaying = false;
        this._duration = 0;
        this._sourceBlob = null;
    }

    /**
     * @desc 获取视频源信息
     * @returns {Object}
     */
    getSourceInfo() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
            duration: this._duration,
        };
    }

    getCurrentTime() {
        if (!this._source) return 0;
        return this._source.currentTime;
    }

    getIsPlaying() {
        return this._isPlaying;
    }

    getSize() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
        };
    }

    getSourceStatus() {
        return this._loadStatus;
    }

    /**
     * @desc 获取视频源blob
     * @returns {Promise<Blob>}
     */
    async getSourceBlob() {
        if (this._sourceBlob) return this._sourceBlob;

        const blob = await FJUtil.getBlobByUrl(this._sourceUrl);
        this._sourceBlob = blob;
        return blob;
    }

    getSourceUrl() {
        return this._source.src;
    }
}

export default FJVideoPlayBase;
